import type { CartItem } from '@/hooks/useCartContext';
import { getTourOverride } from '@/lib/adminStore';
import { tours as catalogTours } from '@/mocks/tours';
import { tourDetails } from '@/mocks/tourDetails';

/** How a tour is booked on the site, chosen per tour in the admin (Paseos → Editar → Reserva). */
export type BookingMode = 'pix' | 'whatsapp';
/** What the tour's price pays for: each person, or the whole private boat or group. */
export type PriceMode = 'person' | 'total';

/**
 * Tours that take an online deposit (Pix or card) unless the admin chose otherwise for them. Every other tour
 * is quoted on WhatsApp: no deposit, no payment step. The payment functions apply the same rule
 * (netlify/lib/bookingMode.mjs).
 */
export const DEFAULT_PIX_SLUGS = [
  'passeio-em-aquamarina-ll', // Escuna Aquamarina II
  'jeep-mata-atlantica', // Jeep pela Mata Atlântica
  'saco-de-mamangua-lancha-rapida', // Lancha pelo Saco do Mamanguá
  'jeep-mata-atlantica-15823', // Praias de Trindade (a repasse: the admin approves each booking)
];

/** Catalog units that price each person; any other unit ("Grupo", "casal", "Escuna privada") is the whole tour. */
const PER_PERSON_UNITS = new Set(['adulto', 'adult', 'pessoa', 'person']);

const catalogEntry = (slug: string) => {
  const target = slug.toLowerCase();
  return [...catalogTours, ...tourDetails].find((tour) => tour.slug.toLowerCase() === target);
};

export function defaultBookingMode(slug: string): BookingMode {
  return DEFAULT_PIX_SLUGS.includes(slug.toLowerCase()) ? 'pix' : 'whatsapp';
}

export function bookingModeOf(slug: string | undefined | null): BookingMode {
  if (!slug) return 'whatsapp';
  const chosen = getTourOverride(slug)?.bookingMode;
  return chosen === 'pix' || chosen === 'whatsapp' ? chosen : defaultBookingMode(slug);
}

export function defaultPriceMode(slug: string): PriceMode {
  const unit = catalogEntry(slug)?.priceUnit?.trim().toLowerCase();
  return !unit || PER_PERSON_UNITS.has(unit) ? 'person' : 'total';
}

export function priceModeOf(slug: string | undefined | null): PriceMode {
  if (!slug) return 'person';
  const chosen = getTourOverride(slug)?.priceMode;
  return chosen === 'person' || chosen === 'total' ? chosen : defaultPriceMode(slug);
}

/** The online checkout prices each person, so a tour priced as a whole boat is always quoted on WhatsApp. */
export function acceptsOnlinePayment(slug: string | undefined | null): boolean {
  return !!slug && bookingModeOf(slug) === 'pix' && priceModeOf(slug) === 'person';
}

/** An order can be paid online only when every tour in it accepts online payment. */
export function orderAcceptsOnlinePayment(items: Pick<CartItem, 'slug'>[]): boolean {
  return items.length > 0 && items.every((item) => acceptsOnlinePayment(item.slug));
}

export type TotalPriceNoun = 'boat' | 'schooner' | 'group';

/** What a whole-tour price pays for, read from the tour's own name: the boat, the schooner or the group. */
export function totalPriceNoun(slug: string): TotalPriceNoun {
  const words = `${slug} ${catalogEntry(slug)?.title ?? ''}`.toLowerCase();
  if (words.includes('escuna')) return 'schooner';
  if (words.includes('lancha')) return 'boat';
  return 'group';
}
